import { ScheduledTask } from "@/data/models/Task";

import {
  PlanningControlSettings,
  OccupiedTimeUsage,
} from "@/data/models/SchedulingSettings";
import { SchedulingModule } from "@/store/modules/SchedulingModule";
import { SchedulingScore } from "./SchedulingObjectiveFunction";
import {
  getTravelTimeToDepotInMs,
  getTravelTimeBetweenTasksInMs,
} from "./TravelTimeProvider";

export interface TimeSlot {
  dayId: string;
  startTimestamp: number;
  durationInMs: number;

  travelTimeToTaskInMs?: number;
  travelTimeFromTaskInMs?: number;

  didTravelFromDepot?: boolean;
  didTravelToDepot?: boolean;

  breakDurationBeforeTaskInMs?: number;
  breakDurationAfterTaskInMs?: number;

  idsOfDaysInSameWeek?: string[];
  ignoreForObjectiveFunctionCalculation?: boolean;

  schedulingScore?: SchedulingScore;
}

export function getDayId(date: Date) {
  return `${date.getDate()}_${date.getMonth()}_${date.getFullYear()}`;
}

export function getDateFromDayId(dayId: string) {
  const tokens = dayId.split("_");

  if (tokens.length != 3) {
    return new Date();
  }

  return new Date(
    Number.parseInt(tokens[2]),
    Number.parseInt(tokens[1]),
    Number.parseInt(tokens[0]),
    1,
    0,
    0
  );
}

export function getWorkingTimeForTimeSlotInMs(
  timeSlot: TimeSlot
) {
    return (
      timeSlot.durationInMs +
      (timeSlot.travelTimeToTaskInMs ?? 0) +
      (timeSlot.travelTimeFromTaskInMs ?? 0)
    );
}

export interface TimeSlotWithScheduledTask extends TimeSlot {
  scheduledTask: ScheduledTask;
}

export function createTimeSlotForSchedueldTask(scheduledTask: ScheduledTask) {
  return {
    scheduledTask,
    dayId: getDayId(scheduledTask.startDate),
    startTimestamp: scheduledTask.startDate.getTime(),
    durationInMs: scheduledTask.durationInMin * 60000,
  };
}

function getValue(value: number | undefined, includeValue: boolean) {
  return includeValue && value ? value : 0;
}

export function getTimeslotStartTimestampIncludingTravelTimeAndBreaks(
  timeSlot: TimeSlot,
  planWithTravelTime: boolean,
  planWithBreaks: boolean
) {
  return (
    timeSlot.startTimestamp -
    (getValue(timeSlot.travelTimeToTaskInMs, planWithTravelTime) +
      getValue(timeSlot.breakDurationBeforeTaskInMs, planWithBreaks))
  );
}

export function getTimeslotEndTimestampIncludingTravelTimeAndBreaks(
  timeSlot: TimeSlot,
  planWithTravelTime: boolean,
  planWithBreaks: boolean
) {
  return (
    timeSlot.startTimestamp +
    timeSlot.durationInMs +
    (getValue(timeSlot.travelTimeFromTaskInMs, planWithTravelTime) +
      getValue(timeSlot.breakDurationAfterTaskInMs, planWithBreaks))
  );
}

export function doTimeSlotsWithSchedueldTasksOverlap(
  a: TimeSlot,
  b: TimeSlot,
  occupiedTimeSettings: PlanningControlSettings
) {
  const planWithTravelTime =
    occupiedTimeSettings.travelTimeUsage == OccupiedTimeUsage.PLAN;

  const planWithBreaks =
    occupiedTimeSettings.breakUsage == OccupiedTimeUsage.PLAN;

  const aStart = getTimeslotStartTimestampIncludingTravelTimeAndBreaks(
    a,
    planWithTravelTime,
    planWithBreaks
  );

  const aEnd = getTimeslotEndTimestampIncludingTravelTimeAndBreaks(
    a,
    planWithTravelTime,
    planWithBreaks
  );

  const bStart = getTimeslotStartTimestampIncludingTravelTimeAndBreaks(
    b,
    planWithTravelTime,
    planWithBreaks
  );

  const bEnd = getTimeslotEndTimestampIncludingTravelTimeAndBreaks(
    b,
    planWithTravelTime,
    planWithBreaks
  );

  let doOverlap = false;

  if (bStart < aStart) {
    doOverlap = bEnd > aStart;
  } else {
    doOverlap = bStart < aEnd;
  }

  return doOverlap;
}

export function fillTravelTimesAndPauseDurations(
  sortedTimeSlots: TimeSlot[],
  earliestLunchBreakTimestamp: number
) {
  const {
    workingHoursSettings,
    planningControlSettings: calculationSettings,
  } = SchedulingModule.schedulingSettings;

  const { breakUsage, travelTimeUsage } = calculationSettings;

  const calculateTravelTimes = travelTimeUsage !== OccupiedTimeUsage.NONE;
  const calculateBreakTimes = breakUsage !== OccupiedTimeUsage.NONE;

  const lastIndex = sortedTimeSlots.length - 1;
  const minToMsFactor = 60000;

  const travelTimeToDepotInMs = getTravelTimeToDepotInMs();
  const travelTimeBetweenTasksInMs = getTravelTimeBetweenTasksInMs();

  let didFindTaskWithLunchBreak = false;

  for (const timeSlot of sortedTimeSlots) {
    let pauseDurationBeforeTask: number | undefined = undefined;
    let pauseDurationAfterTask: number | undefined = undefined;

    if (calculateBreakTimes) {
      if (
        timeSlot.startTimestamp >= earliestLunchBreakTimestamp &&
        !didFindTaskWithLunchBreak
      ) {
        didFindTaskWithLunchBreak = true;
        pauseDurationBeforeTask =
          workingHoursSettings.lunchBreakDurationInMin * minToMsFactor;
      }

      if (
        timeSlot.startTimestamp + timeSlot.durationInMs >=
          earliestLunchBreakTimestamp &&
        !didFindTaskWithLunchBreak
      ) {
        didFindTaskWithLunchBreak = true;

        if (timeSlot.startTimestamp <= earliestLunchBreakTimestamp) {
          pauseDurationAfterTask =
            workingHoursSettings.lunchBreakDurationInMin * minToMsFactor;
        } else {
          pauseDurationBeforeTask =
            workingHoursSettings.lunchBreakDurationInMin * minToMsFactor;
        }
      }
    }

    timeSlot.travelTimeToTaskInMs = undefined;
    timeSlot.travelTimeFromTaskInMs = undefined;

    timeSlot.breakDurationBeforeTaskInMs = pauseDurationBeforeTask;
    timeSlot.breakDurationAfterTaskInMs = pauseDurationAfterTask;
  }

  let isAtDepot = true;

  sortedTimeSlots.forEach((timeSlot, index) => {
    let travelTimeToTask: number | undefined = undefined;
    let travelTimeFromTask: number | undefined = undefined;

    let didTravelFromDepot: boolean | undefined = undefined;
    let didTravelToDepot: boolean | undefined = undefined;

    if (calculateTravelTimes) {
      if (isAtDepot) {
        travelTimeToTask = travelTimeToDepotInMs;
        didTravelFromDepot = true;
        isAtDepot = false;
      }

      if (index == lastIndex) {
        didTravelToDepot = true;
        travelTimeFromTask = travelTimeToDepotInMs;
      } else {
        const followingTask = sortedTimeSlots[index + 1];
        const timeBetweenTasksInMs =
          getTimeslotStartTimestampIncludingTravelTimeAndBreaks(
            followingTask,
            false,
            true
          ) -
          getTimeslotEndTimestampIncludingTravelTimeAndBreaks(
            timeSlot,
            false,
            true
          );

        if (timeBetweenTasksInMs > 2 * travelTimeToDepotInMs) {
          didTravelToDepot = true;
          isAtDepot = true;
          travelTimeFromTask = travelTimeToDepotInMs;
        } else {
          travelTimeFromTask = travelTimeBetweenTasksInMs;
        }
      }
    }

    timeSlot.travelTimeToTaskInMs = travelTimeToTask;
    timeSlot.travelTimeFromTaskInMs = travelTimeFromTask;

    timeSlot.didTravelFromDepot = didTravelFromDepot;
    timeSlot.didTravelToDepot = didTravelToDepot;
  });
}
